






































































































































































































































































































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { Validations } from "vuelidate-property-decorators";
import { required } from "vuelidate/lib/validators";
import { Address } from "@/models/address/address";
import {
  ShippingAddressType,
  DeliveryServices
} from "@/models/order/helpers";
import { NetSuiteCustomerAddress } from "@/models/netsuite/netsuite";
import FormValidation from "@/components/elements/form/FormValidation.vue";
import countries from "@/models/form/data";
import states from "@/models/form/state-abbrv";
import provinces from "@/models/form/prov-abbrv";
import { EventBus } from "@/event-bus";

@Component({
  components: {
    FormValidation
  }
})
export default class Shipping extends Vue {
  protected countries = countries;
  protected country = "";
  protected selectedAddressId: number | "new" | null = null;

  // Freight estimate fields
  protected freightEstimate: boolean | null = null;
  protected freightAddressType: ShippingAddressType | "" = "";
  protected freightDeliveryService: DeliveryServices | "" = "";
  protected submitted = false;
  protected shippingAddressTypeOptions: string[] = Object.values(
    ShippingAddressType
  );

  @Prop() label!: string;
  @Prop() addressee!: string;
  @Prop() attention!: string;
  @Prop() address!: Address;
  @Prop({ default: () => [] }) addresses!: NetSuiteCustomerAddress[];
  @Prop({ default: true }) isNewCustomer!: boolean;
  @Prop({ default: "" }) freight_instructions!: string;

  @Validations()
  validations() {
    const freightRequired = this.freightEstimate === true;
    return {
      label: {},
      addressee: {},
      address: {
        address_1: { required },
        city: { required },
        state: {
          stateReq: value => {
            if (
              !value &&
              (this.address.country === "US" ||
                this.address.country === "Canada")
            ) {
              return false;
            } else {
              return true;
            }
          }
        },
        postal_code: { required },
        country: { required }
      },
      freightEstimate: { required },
      freightAddressType: { required },
      freightDeliveryService: freightRequired ? { required } : {}
    };
  }

  @Watch("$v.$invalid", { deep: true })
  onFormChange(): void {
    if (this.submitted) {
      this.validateForm();
    }
  }

  @Watch("country")
  public updateCountry() {
    const newAddress = Object.assign(
      {},
      { ...this.address },
      { country: this.country }
    );
    this.$emit(`update:address`, newAddress);
    if (this.submitted && this.$v.address["country"]) {
      this.$v.address["country"]!.$touch();
    }
    if (this.country !== "US" && this.country !== "Canada") {
      this.updateAddress({ state: "" }, "state");
    }
  }

  created() {
    EventBus.$on("submitCheckoutForm", this.validateForm);
    this.country = this.address.country ? this.address.country : "US";
    // If addresses are already loaded (e.g. returning from review), restore the selected radio
    if (this.addresses.length > 0) {
      const match = this.addresses.find(
        a =>
          a.addr1 === this.address.address_1 &&
          a.zip === this.address.postal_code
      );
      this.selectedAddressId = match ? match.address_id : "new";
    } else {
      this.selectedAddressId = "new";
    }
    // Restore freight estimate state from prop if returning from review
    if (this.freight_instructions) {
      const addrMatch = this.freight_instructions.match(
        /Address Type - ([^.]+)\./
      );
      const freightMatch = this.freight_instructions.match(
        /Freight Estimate - ([^.]+)\./
      );
      const svcMatch = this.freight_instructions.match(/Services - ([^.]+)\./);
      this.freightAddressType = (addrMatch?.[1] ?? "") as
        | ShippingAddressType
        | "";
      this.freightEstimate = (freightMatch?.[1] ?? "Yes") === "Yes";
      this.freightDeliveryService = (svcMatch?.[1] ?? "") as
        | DeliveryServices
        | "";
    }
  }

  @Watch("addresses")
  onAddressesLoaded(addresses: NetSuiteCustomerAddress[]): void {
    if (addresses.length > 0) {
      // If freight state is already restored (returning from review), don't overwrite it
      if (this.freightAddressType) return;
      const defaultAddr =
        addresses.find(a => a.is_default_shipping) ?? addresses[0];
      this.selectedAddressId = defaultAddr.address_id;
      this.applySelectedAddress(defaultAddr);
    }
  }

  beforeDestroy() {
    EventBus.$off("submitCheckoutForm");
  }

  get zipMask(): string {
    let mask;
    if (this.country === "Canada") {
      mask = "A#A #A#";
    } else {
      mask = "#####";
    }
    return mask;
  }

  get states(): string[] {
    let list = ["--"];
    if (this.country === "Canada") {
      list = provinces;
    } else if (this.country === "US") {
      list = states;
    }
    return list;
  }

  get isFreightResidential(): boolean {
    return this.freightAddressType === ShippingAddressType.Residential;
  }

  get deliveryServiceOptions(): string[] {
    if (this.isFreightResidential) {
      return [DeliveryServices.Both];
    }
    return Object.values(DeliveryServices);
  }

  protected onFreightEstimateChange(): void {
    if (this.freightEstimate === false) {
      this.freightDeliveryService = "";
      this.emitFreightInstructions();
    } else if (this.freightEstimate === true && this.isFreightResidential) {
      this.freightDeliveryService = DeliveryServices.Both;
      this.emitFreightInstructions();
    }
    if (this.submitted) {
      this.$v.freightEstimate.$touch();
      this.validateForm();
    }
  }

  protected onFreightAddressTypeChange(): void {
    if (this.isFreightResidential) {
      this.freightDeliveryService = DeliveryServices.Both;
    } else {
      this.freightDeliveryService = "";
    }
    if (this.submitted) {
      this.$v.freightAddressType.$touch();
    }
    this.emitFreightInstructions();
  }

  protected emitFreightInstructions(): void {
    if (this.freightAddressType) {
      let instructions = `Address Type - ${this.freightAddressType}.`;
      instructions += ` Freight Estimate - ${this.freightEstimate ? "Yes" : "No"}.`;
      if (this.freightEstimate === true && this.freightDeliveryService) {
        instructions += ` Services - ${this.freightDeliveryService}.`;
      }
      this.$emit("update:freight_instructions", instructions);
    }
    if (this.submitted) {
      this.$v.freightDeliveryService.$touch();
      this.validateForm();
    }
  }

  protected applySelectedAddress(addr: NetSuiteCustomerAddress): void {
    // Emit all field updates without triggering intermediate validations.
    // Props update asynchronously, so validating mid-batch sees stale values.
    this.$emit("update:label", addr.label ?? "");
    this.$emit("update:addressee", addr.addressee ?? "");
    this.$emit("update:attention", addr.attention ?? "");
    const newAddress: any = {
      address_1: addr.addr1 ?? "",
      address_2: addr.addr2 ?? "",
      address_3: addr.addr3 ?? "",
      city: addr.city ?? "",
      state: addr.state ?? "",
      postal_code: addr.zip ?? "",
      country: addr.country ?? "US"
    };
    this.$emit("update:address", newAddress);
    this.country = addr.country ?? "US";
    // Auto-populate address type from the saved NetSuite address
    this.freightAddressType = addr.is_residential
      ? ShippingAddressType.Residential
      : ShippingAddressType.Commercial;
    if (addr.is_residential) {
      this.freightDeliveryService = DeliveryServices.Both;
    } else {
      this.freightDeliveryService = "";
    }
    this.emitFreightInstructions();
    // Validate once after Vue has propagated the new prop values
    if (this.submitted) {
      this.$nextTick(() => {
        this.$v.$touch();
        this.validateForm();
      });
    }
  }

  protected onAddressSelect(addressId: number | "new"): void {
    this.selectedAddressId = addressId;
    if (addressId === "new") {
      this.updateItem("", "label");
      this.updateItem("", "addressee");
      this.updateItem("", "attention");
      this.updateAddress(
        {
          address_1: "",
          address_2: "",
          address_3: "",
          city: "",
          state: "",
          postal_code: ""
        },
        "address_1"
      );
      this.country = "US";
      this.freightAddressType = "";
      this.freightDeliveryService = "";
      this.$emit("update:freight_instructions", "");
    } else {
      const addr = this.addresses.find(a => a.address_id === addressId);
      if (addr) this.applySelectedAddress(addr);
    }
  }

  protected validateForm(): void {
    this.submitted = true;
    this.$v.$touch();
    this.$emit("sendError", this.$v.$invalid, "shipping");
  }

  /**
   * Have to explicitly call vuelidate ($v) methods if not using v-model
   */
  public updateItem(fieldValue: string, fieldName: string) {
    this.$emit(`update:${fieldName}`, fieldValue);
    if (this.submitted && this.$v[fieldName]) {
      this.$v[fieldName]!.$touch();
      this.validateForm();
    }
  }

  /**
   * Cannot update a nested property in an object due to the way Vue tracks nested data
   * i.e. update:address.address_1 = value will not trigger reactivity, so use Object.assign{} and update entire address object
   */
  public updateAddress(newObj: any, fieldName: string) {
    const newAddress = Object.assign(
      {},
      { ...this.address, country: this.country },
      { ...newObj }
    );
    this.$emit(`update:address`, newAddress);
    if (this.submitted && this.$v.address[fieldName]) {
      this.$v.address[fieldName]!.$touch();
      this.validateForm();
    }
  }
}
